import { reduce as _reduce } from 'lodash';
import { uuidv4 } from '@graphistry/node-util/uuid';
import { ref as $ref } from '@graphistry/falcor-json-graph';
import { getDefaultQueryForDataType } from '@graphistry/viz/src/models/expressions';
import { opt } from '../views';

const logger = require('@graphistry/logger')(module);


export const legendTypeHistogramColumn = 'type';
export const legendPivotHistogramColumn = 'Pivot';
export const timebarHistogramColumn = 'time';
export function histograms(view, options = {}) {

    options = options.vizClientParams || {};

    return {
        histogramsById: {},
        histograms: {
            length: 0,
            visible: opt(options, 'showHistograms', Boolean, true),
            id: 'histograms',
            name: 'Histograms',
            // legend: $ref(`${view}.legend`),
            templates: $ref(`${view}.columns`),
            controls: [
                {
                    selected: opt(options, 'showHistograms', Boolean, true),
                    id: 'toggle-histograms',
                    name: 'Histograms',
                    items: $ref(`${view}.histograms`)
                }
            ],
            encodings: $ref(`${view}.encodings`)
        }
    };
}

/*
    id: string,
    name: string,
    yScale: string,
    binType: string,
    numBins: number,
    dataType: string,
    binWidth: number,
    isMasked: boolean,
    identifier: string,
    numElements: number,
    maxElements: number,
    componentType: string,
    bins: [{
        count: int,
        exclude: boolean,
        //   empty | value | range
        values: [] | [int] | [int, int]
    } (,) ...]
*/

export function histogram(
    { name = 'degree', yScale = 'none', dataType = 'number', componentType = 'point' } = {},
    histogramId = uuidv4()
) {

    logger.trace('Make model histogram', { name, dataType });

    return {
        range: [],
        id: histogramId,
        name /* degree */,
        yScale /* none, log, log2 log10 */,
        dataType /* number */,
        componentType /* edge | point */,
        identifier: `${componentType}:${name}`
    };
}

export function histogramFilterQuery(histogram, binIndexes = [], isCategorical = false) {
    let query;
    const { bins, binType, dataType, minValue, binWidth, identifier, componentType } = histogram;

    logger.trace('histogramFilterQuery', histogram, binIndexes);

    if (binType === 'nodata' || binIndexes.length <= 0) {
        return null;
    } else if (binIndexes.length === 1) {
        // In the base case, return a query for a single bin
        return histogramBinQuery(histogram, bins[binIndexes[0]], true);
    } else if (dataType !== 'bool' && (binType === 'time' || binType === 'histogram' || !isCategorical)) {
        // If there's more than one bin index and the histogram supports ranges
        // (binType === 'histogram'), return a query between the min and max bin values
        const vals = [].concat(...binIndexes.map(i => bins[i].values)).filter(v => ! isNaN(v));
        return histogramBinQuery(histogram, {
            exclude: false,
            values: [Math.min(...vals), Math.max(...vals)]
        });
    }

    // The complex case is creating a query for 'countBy' bin values,
    // and possibly exclude the `_other` bin's value.

    const { exclude, values } = binIndexes.reduce(
        ({ exclude, values }, i, x, xs, bin = bins[i]) => ({
            exclude: exclude || bin.exclude,
            values: bin.exclude ? values : [...values, ...bin.values]
        }),
        { exclude: false, values: [] }
    );

    query = getDefaultQueryForDataType({
        queryType: 'isOneOf',
        identifier,
        values
    });

    if (exclude) {
        query.ast = {
            operator: 'OR',
            type: 'BinaryPredicate',
            left: {
                operator: 'NOT',
                type: 'NotExpression',
                value: getDefaultQueryForDataType({
                    identifier,
                    queryType: 'isOneOf',
                    values: _reduce(
                        bins,
                        (vals, { exclude, values } = {}) => {
                            if (!exclude && values && values.length) {
                                vals.push.apply(vals, values);
                            }
                            return vals;
                        },
                        []
                    )
                }).ast
            },
            right: query.ast
        };
    }

    return {
        ...query,
        type: componentType,
        attribute: identifier
    };
}

export function histogramBinQuery(histogram, bin, shouldCoerceTypeToBinaryPredicate = false) {
    const { values = [], exclude = false } = bin;
    const { bins = [], binType, identifier, componentType } = histogram;

    logger.trace('histogramBinQuery', histogram, { bin, shouldCoerceTypeToBinaryPredicate });


    const queryProperties = {
        identifier,
        stop: values[1],
        start: values[0],
        value: values[0],
        dataType: histogram.dataType || undefined,
        queryType: exclude ? 'isOneOf' : values.length === 1 ? 'isEqualTo' : 'isBetween',
        values: !exclude
            ? undefined
            : _reduce(
                  bins,
                  (vals, { exclude, values } = {}) => {
                      if (!exclude && values && values.length) {
                          vals.push.apply(vals, values);
                      }
                      return vals;
                  },
                  []
              )
    };

    const query = getDefaultQueryForDataType(queryProperties);
    if (exclude) {
        query.ast = {
            type: 'NotExpression',
            operator: 'NOT',
            value: query.ast
        };
    }
    logger.trace('////histogramBinQuery query', query);

    return {
        ...query,
        type: componentType,
        attribute: identifier
    };    
}

export function histogramBinHighlightQuery(histogram, bin) {
    const { identifier } = histogram;
    if (!identifier) {
        const err = new Error('histogramBinHighlightQuery: Making a histogram without identifier');
        logger.error({ histogram, bin, err });
        throw err;
    }
    const query = histogramBinQuery(histogram, bin);
    query.ast = {
        type: 'BinaryPredicate',
        operator: 'AND',
        left: {
            type: 'UnaryExpression',
            operator: 'DEFINED',
            fixity: 'postfix',
            argument: { type: 'Identifier', name: identifier }
        },
        right: query.ast
    };
    return query;
}
